import { cloneDeep, merge } from 'lodash'
import { useEffect } from 'react'

import useGetOrPostChatbotSettings from '~/features/agents/hooks/useGetOrPostChatbotSettings'
import useUpdateAgent from '~/features/agents/hooks/useUpdateAgent'
import useHasValidAuthSession from '~/features/auth/hooks/useHasValidAuthSession'
import useUserAccess from '~/features/auth/hooks/useUserAccess'
import { defaultChatBotLayout } from '~/features/settings/components/chatBot/utils'
import { EChatbotTheme } from '~/features/settings/models'
import { sessionStore } from '~/store'

/**
 * Reset chatbot settings for users who are not on a paid plan
 * This scenario is for users who have custom chatbot settings and are not on a paid plan
 * Use Case: A user has a subscription expired and hence they are not on a paid plan, or they have downgraded their plan
 */

const useResetChatbotSettings = () => {
  const layoutData = useGetOrPostChatbotSettings()

  const { isPlusUser, isBasicUser, isBasicFreeTrial } = useUserAccess()

  const isPaidUser = isPlusUser || isBasicFreeTrial || isBasicUser

  const agentId = sessionStore((state) => state.agent.id) || ''
  const { mutate: updateAgentMutation } = useUpdateAgent(agentId)
  const { hasValidAuthSession } = useHasValidAuthSession()

  useEffect(() => {
    const isCustomLinkView =
      layoutData?.selection.custom?.[EChatbotTheme.linkView] ?? false
    const isCustomSimple =
      layoutData?.selection.custom?.[EChatbotTheme.simple] ?? false

    if (!isPaidUser && (isCustomLinkView || isCustomSimple)) {
      const resetLayout = merge({}, defaultChatBotLayout, {
        selection: {
          theme: layoutData?.selection.theme,
          [EChatbotTheme.simple]: isCustomSimple
            ? defaultChatBotLayout.selection[EChatbotTheme.simple]
            : layoutData?.selection[EChatbotTheme.simple],
          [EChatbotTheme.linkView]:
            layoutData?.selection[EChatbotTheme.linkView],
        },
        ...(isCustomLinkView && {
          options: cloneDeep(defaultChatBotLayout.options),
        }),
      })

      if (!hasValidAuthSession) return

      updateAgentMutation({
        payload: {
          chatSettings: {
            layout: resetLayout,
          },
        },
      })
    }
  }, [
    isPaidUser,
    agentId,
    updateAgentMutation,
    layoutData?.selection,
    hasValidAuthSession,
  ])
}

export default useResetChatbotSettings
